import { Injectable } from '@angular/core';
import { Environment, ENVIRONMENT } from 'app/environment/environment';

@Injectable()
export class GravatarService {
  private readonly clientUrl: string;
  private readonly environment: Environment;

  constructor() {
    // private readonly environment: Environment
    this.environment = ENVIRONMENT; // TODO: Inject instead (doesn't work): "Can't resolve all parameters for GravatarService: (?)."
    this.clientUrl = this.environment.selfBaseUrl;
  }

  private readonly emailUrlCache = new Map<string, Promise<string>>();

  /**
   * Gravatar URL for a raw email address, hashed client-side with SHA-256
   * (supported by Gravatar alongside MD5). Cached per email so repeated rows
   * hash once. Resolves to '' when hashing is unavailable (non-secure context).
   *
   * Requests default=404 so callers can detect "no gravatar account" via the
   * img error event and render their own fallback icon instead.
   */
  public getUrlForEmail(email: string): Promise<string> {
    const normalized = email?.trim().toLowerCase();
    if (!normalized || !crypto?.subtle) return Promise.resolve('');
    let cached = this.emailUrlCache.get(normalized);
    if (!cached) {
      cached = crypto.subtle.digest('SHA-256', new TextEncoder().encode(normalized)).then((digest) => {
        const hash = Array.from(new Uint8Array(digest))
          .map((byte) => byte.toString(16).padStart(2, '0'))
          .join('');
        return 'https://0.gravatar.com/avatar/' + hash + '?default=404';
      });
      this.emailUrlCache.set(normalized, cached);
    }
    return cached;
  }

  public getFullUrl(gravatarHash: string): string {
    const gravatarUrl = 'https://0.gravatar.com/avatar/' + gravatarHash;
    let baseUrl = this.clientUrl;
    if (this.environment.isDevelopment) {
      // Gravatar default-image doesn't work with localhost:8080, so get the image from staging instead
      baseUrl = 'https://staging.triggerbee.com';
    }
    const fallbackUrl = encodeURIComponent(baseUrl + '/assets/img/icons/user.png');

    return gravatarUrl + '?default=' + fallbackUrl;
  }

  public getCustomerProfilesUrl(gravatarHash: string, type: string) {
    const gravatarUrl = 'https://0.gravatar.com/avatar/' + gravatarHash;
    let baseUrl = this.clientUrl;
    if (this.environment.isDevelopment) {
      // Gravatar default-image doesn't work with localhost:8080, so get the image from staging instead
      baseUrl = 'https://staging.triggerbee.com';
    }
    let fallbackUrl;
    if (type === 'session') {
      fallbackUrl = encodeURIComponent(baseUrl + '/assets/img/icons/offline_bolt.png');
    } else {
      fallbackUrl = encodeURIComponent(baseUrl + '/assets/img/icons/account_circle.png');
    }

    return gravatarUrl + '?default=' + fallbackUrl;
  }
}
